import {
  collection,
  DocumentData,
  getDocs,
  limit,
  QueryDocumentSnapshot,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "./config";
import { withCache } from "../utils/cache";

export interface PublicAthlete {
  id: string;
  athleteId: string;
  firstName: string;
  lastName: string;
  slug: string;
  imageUrl?: string;
  club?: string;
  currentClub?: string;
  currentOrganizationId?: string;
  nationality?: string;
  countryCode?: string;
  gender?: string;
  competitorType?: "FEDERATION_MEMBER" | "EXTERNAL_COMPETITOR";
  rankingEligible?: boolean;
  publicationStatus: "PUBLISHED";
}

export interface PublicOrganization {
  id: string;
  sourceOrganizationId: string;
  slug: string;
  officialName: string;
  shortName?: string;
  type: "CLUB" | "FOREIGN_CLUB";
  countryCode?: string;
  countryName?: string;
  location?: string;
  imageUrl?: string;
  external?: boolean;
  status: "ACTIVE";
}

export interface PublicCoach {
  id: string;
  organizationId: string;
  displayName: string;
}

export interface DirectoryPage<T> {
  items: T[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export function normalizePublicSearch(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("ka-GE").slice(0, 80);
}

const publishedAthlete = (row: QueryDocumentSnapshot<DocumentData>): PublicAthlete | null => {
  const value = row.data();
  return value.publicationStatus === "PUBLISHED" && value.status === "ACTIVE"
    ? ({ id: row.id, ...value } as PublicAthlete)
    : null;
};

export async function getPublicAthletePage(options: {
  search?: string;
  cursor?: QueryDocumentSnapshot<DocumentData> | null;
  pageSize?: number;
} = {}): Promise<DirectoryPage<PublicAthlete>> {
  const pageSize = Math.min(Math.max(options.pageSize || 24, 1), 48);
  const search = normalizePublicSearch(options.search || "");
  const constraints: any[] = search
    ? [where("searchTokens", "array-contains", search)]
    : [where("publicationStatus", "==", "PUBLISHED")];
  if (options.cursor) constraints.push(startAfter(options.cursor));
  constraints.push(limit(pageSize + 1));
  const cacheKey = `public-directory:athletes:${search}:${options.cursor?.id || "first"}:${pageSize}`;
  const snap = await withCache(cacheKey, 60 * 1000, () => getDocs(query(collection(db, "publicAthletes"), ...constraints)));
  const visibleRows = snap.docs.slice(0, pageSize);
  const items = visibleRows.map(publishedAthlete).filter(Boolean) as PublicAthlete[];
  items.sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
  return { items, cursor: visibleRows.length ? visibleRows[visibleRows.length - 1] : null, hasMore: snap.docs.length > pageSize };
}

export async function getPublicAthleteBySlug(slug: string): Promise<PublicAthlete | null> {
  const snap = await getDocs(query(collection(db, "publicAthletes"), where("slug", "==", slug), limit(2)));
  if (snap.size !== 1) return null;
  return publishedAthlete(snap.docs[0]);
}

export async function getPublicAthleteByFederationId(athleteId: string): Promise<PublicAthlete | null> {
  const snap = await getDocs(query(collection(db, "publicAthletes"), where("athleteId", "==", athleteId), limit(2)));
  const matches = snap.docs.map(publishedAthlete).filter(Boolean) as PublicAthlete[];
  return matches.length === 1 ? matches[0] : null;
}

export async function getPublishedAthleteLinks(athleteIds: string[]): Promise<Record<string, string>> {
  const ids = [...new Set(athleteIds.filter(Boolean))];
  const result: Record<string, string> = {};
  for (let index = 0; index < ids.length; index += 30) {
    const snap = await getDocs(query(collection(db, "publicAthletes"), where("athleteId", "in", ids.slice(index, index + 30))));
    snap.docs.forEach((row) => {
      const athlete = publishedAthlete(row);
      if (athlete) result[athlete.athleteId] = athlete.slug;
    });
  }
  return result;
}

export async function getPublicClubPage(options: { search?: string; pageSize?: number } = {}): Promise<PublicOrganization[]> {
  const search = normalizePublicSearch(options.search || "");
  const constraints: any[] = search ? [where("searchTokens", "array-contains", search)] : [];
  constraints.push(limit(Math.min(Math.max(options.pageSize || 60, 1), 100)));
  const pageSize = Math.min(Math.max(options.pageSize || 60, 1), 100);
  const cacheKey = `public-directory:clubs:${search}:${pageSize}`;
  const snap = await withCache(cacheKey, 60 * 1000, () => getDocs(query(collection(db, "publicOrganizations"), ...constraints)));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as PublicOrganization))
    .filter((row) => row.status === "ACTIVE" && ["CLUB", "FOREIGN_CLUB"].includes(row.type))
    .sort((a, b) => a.officialName.localeCompare(b.officialName));
}

export async function getPublicClubBySlug(slug: string): Promise<PublicOrganization | null> {
  const snap = await getDocs(query(collection(db, "publicOrganizations"), where("slug", "==", slug), limit(2)));
  if (snap.size !== 1) return null;
  const value = { id: snap.docs[0].id, ...snap.docs[0].data() } as PublicOrganization;
  return value.status === "ACTIVE" ? value : null;
}

export async function getPublicClubRoster(organizationId: string): Promise<PublicAthlete[]> {
  const snap = await getDocs(query(collection(db, "publicAthletes"), where("currentOrganizationId", "==", organizationId), limit(200)));
  return snap.docs.map(publishedAthlete).filter(Boolean)
    .sort((a, b) => a!.lastName.localeCompare(b!.lastName) || a!.firstName.localeCompare(b!.firstName)) as PublicAthlete[];
}

export async function getPublicClubCoaches(organizationId: string): Promise<PublicCoach[]> {
  const snap = await getDocs(query(collection(db, "publicOrganizationCoaches"), where("organizationId", "==", organizationId), limit(20)));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as PublicCoach))
    .filter((row) => Boolean(row.displayName))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}
